import { useState, useEffect } from 'react';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { TaskGridRowData } from '../../../../../@types/crs/case';
import { getTaskGridRows, searchIfContainedInObj } from 'src/careflow/helpers/common';
import {
  Reference,
  Task,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import useGridFilters, { GridFilters } from 'src/careflow/ph-core/grid/hook/useGridFilters';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import moment from 'moment';
import { isBetweenDates } from 'src/utils/dates';
import { WrappedGoal } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Goal';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import { WrappedHealthcareService } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/HealthcareService';
import { Box, CardContent } from '@mui/material';
import SeverityStatus, { AlertSeverity } from 'src/components/SeverityStatus';
import useObjectState from 'src/hooks/useObjectState';
import { WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { WrappedCondition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { TaskByPathway } from 'src/careflow/care-coordination/task/patient/TasksByPathway/TasksByPathway';
import { TaskOpenFrom } from 'src/careflow/care-coordination/task/patient/TasksGrid/TaskModal';
import TasksGrid, { initialTaskFilters, TaskFilters } from 'src/careflow/care-coordination/task/patient/TasksGrid/TasksGrid';

const pathwayAll: ValueSetComposeIncludeConcept = {
  code: 'all',
  display: 'All',
};

interface TaskByPathwayState {
  selectedPathway: string;
}

export interface CarePlanTaskGridProps {
  tasks: WrappedTask[];
  patient: WrappedPatient | null;
  carePlan?: WrappedCarePlan | null;
  goal?: WrappedGoal | null;
  taskBasedOn?: WrappedTask | null;
  healthCareServices?: WrappedHealthcareService[] | null;
  serviceRequests?: WrappedServiceRequest[];
  isNestedGrid?: boolean;
  showPagination?: boolean;
  title?: string;
  buttonTitle?: string;
  onAssessmentSelect?: (task: Task) => void;
  pathwaysList?: TaskByPathway[];
  handleItem?: (items: any) => void;
  includeSubTasks?: boolean;
  openFrom?: TaskOpenFrom;
  goalTaskSubTask?: boolean;
  conditions?: WrappedCondition[];
  observations?: WrappedObservation[];
  carePlanTaskIds?: string[];
  allTasksEnabled?: boolean;
  childrenButtons?: React.ReactNode;
}

const CaseTaskGrid = ({
  tasks,
  isNestedGrid,
  taskBasedOn,
  showPagination,
  patient,
  carePlan,
  goal,
  healthCareServices,
  serviceRequests,
  title,
  buttonTitle,
  onAssessmentSelect,
  pathwaysList,
  handleItem,
  includeSubTasks = true,
  openFrom, 
  goalTaskSubTask,
  conditions,
  observations,
  carePlanTaskIds,
  allTasksEnabled,
  childrenButtons
}: CarePlanTaskGridProps) => {
  const { configurations, componentsData } = useTenantConfigData();
  const { filters: taskConfigFilters, statusCountByPathway } = componentsData?.task ?? {};
  
  const [{ selectedPathway }, updateState] = useObjectState<TaskByPathwayState>({
    selectedPathway: 'all',
  });

  const handlePathwayTabChange = (_: React.SyntheticEvent, pathway: string) => {
    updateState({
      selectedPathway: pathway,
    });
  };

  const {
    isFilterDrawerOpen,
    onFilterDrawerOpen,
    onFilterDrawerClose,
    onSearchTextFieldChange,
    searchTextFieldValue,
    filters,
    onApplyFilters,
  } = useGridFilters<TaskFilters>(initialTaskFilters);
  const [taskGridRows, setTasksGridRows] = useState<TaskGridRowData[]>([]);
  const [owners, setOwners] = useState<Reference[]>([]);

  const {
    valueSets: [taskStatuses],
  } = useValueSetsByIdentifiers(['ph-task-status']);

  useEffect(() => {
    setOwners(
      tasks?.reduce<Reference[]>((owners, { owner }) => {
        if (!owner) return owners;
        if (owners.find(({ reference }) => reference === owner?.reference)) return owners;
        return [...owners, owner];
      }, [])
    );
  }, [tasks]);

  useEffect(() => {
    const taskGridRows = getTaskGridRows(tasks, taskStatuses?.asList?.() ?? [], serviceRequests ?? [], configurations?.fhir);
    if (handleItem) handleItem(getStatusCountByPathway(taskGridRows, statusCountByPathway));
    setTasksGridRows(taskGridRows);
  }, [tasks, serviceRequests, configurations]);

  useEffect(() => {
    if(taskConfigFilters?.status){
      onApplyFilters({ status: taskConfigFilters?.status })
    }
  },[taskConfigFilters?.status]);

  return (
    <>
      <CardContent className={'cardContent'} sx={{ padding: 0 }}>
        <Box sx={{ marginTop: 2, paddingBottom: 0 }}>
          {pathwaysList ? (
            pathwaysList?.map((pathway) => {
              const { code, id } = pathway;
              const taskGridRowsByPathway = getTaskGridRows(
                getGoalsByPathWay(code, taskGridRows, id),
                taskStatuses?.asList?.() ?? [],
                serviceRequests ?? [],
                configurations?.fhir
              );
              return (
                <TabPanel key={code} pathway={code} selectedPathway={selectedPathway}>
                  <TasksGrid
                    includeSubTasks={includeSubTasks}
                    patient={patient}
                    tasks={taskGridRowsByPathway}
                    owners={owners}
                    goal={goal}
                    searchTextFieldValue={searchTextFieldValue}
                    onSearchTextFieldChange={onSearchTextFieldChange}
                    isFilterDrawerOpen={isFilterDrawerOpen}
                    onFilterDrawerOpen={onFilterDrawerOpen}
                    onFilterDrawerClose={onFilterDrawerClose}
                    onApplyFilters={onApplyFilters}
                    filterValues={filters}
                    isNestedGrid={isNestedGrid}
                    showPagination={showPagination}
                    carePlan={carePlan}
                    healthCareServices={healthCareServices}
                    title={title}
                    buttonTitle={buttonTitle}
                    taskBasedOn={taskBasedOn}
                    onAssessmentSelect={onAssessmentSelect}
                    handlePathwayTabChange={handlePathwayTabChange}
                    selectedPathway={selectedPathway}
                    pathwaysList={pathwaysList}
                    openFrom={openFrom}
                    conditions={conditions}
                    observations={observations}
                    carePlanTaskIds={carePlanTaskIds}
                    allTasksEnabled={allTasksEnabled}
                    childrenButtons={childrenButtons}
                  />
                </TabPanel>
              );
            })
          ) : (
            <TasksGrid
              includeSubTasks={includeSubTasks}
              patient={patient}
              tasks={taskGridRows}
              owners={owners}
              goal={goal}
              searchTextFieldValue={searchTextFieldValue}
              onSearchTextFieldChange={onSearchTextFieldChange}
              isFilterDrawerOpen={isFilterDrawerOpen}
              onFilterDrawerOpen={onFilterDrawerOpen}
              onFilterDrawerClose={onFilterDrawerClose}
              onApplyFilters={onApplyFilters}
              filterValues={filters}
              isNestedGrid={isNestedGrid}
              showPagination={false}
              carePlan={carePlan}
              healthCareServices={healthCareServices}
              title={title}
              buttonTitle={buttonTitle}
              taskBasedOn={taskBasedOn}
              onAssessmentSelect={onAssessmentSelect}
              handlePathwayTabChange={handlePathwayTabChange}
              selectedPathway={selectedPathway}
              pathwaysList={pathwaysList}
              openFrom={openFrom}
              goalTaskSubTask={goalTaskSubTask}
              conditions={conditions}
              observations={observations}
              carePlanTaskIds={carePlanTaskIds}
              allTasksEnabled={allTasksEnabled}
              childrenButtons={childrenButtons}
            />
          )}
        </Box>
      </CardContent>
    </>
  );
};

export const filterTaskGridRows = (
  taskGridRows: TaskGridRowData[],
  { filters, searchTextFieldValue }: GridFilters<TaskFilters>,
  carePlanTaskIds?: string[]
): TaskGridRowData[] => {
  const { status, startDate, endDate, selectedOwners, linked } = filters;
  let taskGridRowsFiltered = taskGridRows?.length > 0 ? [...taskGridRows] : [];
  const searchByString = searchTextFieldValue?.toLowerCase().trim() ?? '';
  if (searchByString.length >= 3) {
    taskGridRowsFiltered = taskGridRowsFiltered.filter((taskGridRow) =>
      searchIfContainedInObj(
        taskGridRow,
        ['task', 'number', 'owner', 'startDate.message', 'endDate.message', 'status'],
        searchByString
      )
    );
  }

  return taskGridRowsFiltered.filter((taskRow) => {
    const task = taskRow.wrappedTask;
    const taskStartDate = moment(task?.getStartDate() ?? null);
    const taskEndDate = moment(task?.getEndDate() ?? null);
    if (startDate?.isValid?.() && endDate?.isValid?.()) {
      if (!taskStartDate.isValid() || !taskEndDate.isValid()) return false;
      if (!isBetweenDates(taskStartDate, taskEndDate, startDate, endDate)) return false;
    }

    if (startDate?.isValid?.()) {
      if (!taskStartDate.isValid()) return false;
      if (!taskStartDate.isSameOrAfter(startDate)) return false;
    }

    if (endDate?.isValid?.()) {
      if (!taskEndDate.isValid()) return false;
      if (!taskEndDate.isSameOrBefore(endDate)) return false;
    }

    if (selectedOwners.length) {
      if (!selectedOwners.find((owner) => task?.owner?.reference === owner.reference)) return false;
    }

    if (status.length) {
      if (!status.find((status) => status.code === task?.status)) return false;
    }
    
    // Filter by linked tasks (tasks that are part of the care plan)
    if (linked) {
      // Check if the task is linked to the care plan by checking if its ID is in carePlanTaskIds
      const taskId = task?.id;
      const isLinked = taskId && carePlanTaskIds?.includes(taskId);
      console.log('Linked filter:', { taskId, isLinked, carePlanTaskIds, linked });
      if (!isLinked) {
        return false;
      }
    }
    
    return true;
  });
};

interface TabPanelProps {
  children: React.ReactNode;
  selectedPathway: string;
  pathway: string | undefined;
}

const TabPanel = ({ children, selectedPathway, pathway }: TabPanelProps): JSX.Element => (
  <>{selectedPathway === pathway ? <>{children}</> : null}</>
);

type DueDateSeverities = AlertSeverity.SUCCESS | AlertSeverity.ERROR | AlertSeverity.WARNING;

export type StatusSeverityCount = Record<DueDateSeverities, number>;

type StatusByConfig = { open: string[], close: string[] };

export const getStatusCountByPathway = (
  tasks: TaskGridRowData[], 
  statusByConfig?: StatusByConfig
): StatusSeverityCount => {
  const openCloseStatus = statusByConfig ? 
    statusByConfig : { 
      open: ['Draft', 'Requested', 'Received', 'Accepted', 'Ready', 'In Progress', 'On Hold'], 
      close: ['Completed', 'Cancelled', 'Failed', 'Rejected', 'Entered In Error']
    };
  const statuses: StatusSeverityCount = {
    [AlertSeverity.ERROR]: 0,
    [AlertSeverity.WARNING]: 0,
    [AlertSeverity.SUCCESS]: 0,
  };
  for (const {
    endDate: { severity },
    status
  } of tasks) {
    if (statuses.hasOwnProperty(severity as AlertSeverity)){
      if (
        severity as DueDateSeverities === AlertSeverity.SUCCESS || 
        openCloseStatus.close.includes(status)
      ){
        statuses[AlertSeverity.SUCCESS as DueDateSeverities]++;}
      else if(
        severity as DueDateSeverities !== AlertSeverity.SUCCESS || 
        openCloseStatus.open.includes(status)
      ){
        statuses[severity as DueDateSeverities]++;
      }
    }
  }
  return statuses;
};

export const getGoalsByPathWay = (
  pathwayCode: string | undefined,
  tasks: TaskGridRowData[],
  healthCareServiceId: string | undefined
) => {
  if (!pathwayCode || pathwayCode === pathwayAll.code) return tasks.map((e) => e.wrappedTask);
  console.log('filtering tasks in getGoalsByPathWay')
  return tasks
    .filter((e) =>
      e?.basedOn?.some((r) => r.reference === `HealthcareService/${healthCareServiceId}`)
    )
    .map((e) => e.wrappedTask);
};

interface SeverityStatusCountProps {
  count: number;
  severity: AlertSeverity;
}

export const SeverityStatusCount = ({ count, severity }: SeverityStatusCountProps) => (
  <SeverityStatus status={{ message: String(count), severity }} sx={{ height: '20px', ml: 0.5 }} />
);

export default CaseTaskGrid;
